/**
 * 装配体配合 transform 矩阵转换工具。
 *
 * 文件职责：
 * 1. 在 Mate 控制器中复用组件 transform 快照与矩阵之间的转换；
 * 2. 为 Lock mate 相对位姿计算提供纯数据工具；
 * 3. 不依赖 Viewer、UI 或 Three.js 场景对象。
 */

import * as THREE from 'three/webgpu';
import type { ComponentTransformSnapshot } from '../../component-instance';

/**
 * 从 transform 组合矩阵。
 *
 * @param transform transform 快照。
 * @returns 变换矩阵。
 */
export function composeAssemblyMateTransformMatrix(
  transform: ComponentTransformSnapshot,
): THREE.Matrix4 {
  return new THREE.Matrix4().compose(
    new THREE.Vector3(transform.position[0], transform.position[1], transform.position[2]),
    new THREE.Quaternion(
      transform.quaternion[0],
      transform.quaternion[1],
      transform.quaternion[2],
      transform.quaternion[3],
    ),
    new THREE.Vector3(transform.scale[0], transform.scale[1], transform.scale[2]),
  );
}

/**
 * 从矩阵还原 transform。
 *
 * @param matrix 变换矩阵。
 * @returns transform 快照。
 */
export function decomposeAssemblyMateTransformMatrix(
  matrix: THREE.Matrix4,
): ComponentTransformSnapshot {
  const position = new THREE.Vector3();
  const quaternion = new THREE.Quaternion();
  const scale = new THREE.Vector3();
  matrix.decompose(position, quaternion, scale);
  return {
    position: [position.x, position.y, position.z] as const,
    quaternion: [quaternion.x, quaternion.y, quaternion.z, quaternion.w] as const,
    scale: [scale.x, scale.y, scale.z] as const,
  };
}
